import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiDollarSign,
  FiClock,
  FiAlertTriangle,
  FiTrendingUp,
  FiArrowRight,
  FiUsers,
  FiDownload,
  FiActivity,
} from 'react-icons/fi';
import PlanUpgradeCard from './PlanUpgradeCard';
import CollectionTrendChart from './CollectionTrendChart';
import CollectionImpactPanel from './CollectionImpactPanel';
import { downloadCollectionExport } from '../utils/collectionExport';

const AGING_LABELS = {
  vigente: 'A vencer',
  '1-30': '1–30 días',
  '31-60': '31–60 días',
  '61+': '61+ días',
};

const AGING_COLORS = {
  vigente: 'bg-sky-500',
  '1-30': 'bg-amber-400',
  '31-60': 'bg-orange-500',
  '61+': 'bg-red-500',
};

function KpiCard({ label, value, hint, color, modoOscuro, icon: Icon, highlight }) {
  return (
    <div className={`px-4 py-3.5 ${
      highlight
        ? modoOscuro ? 'bg-[#1F80FF]/10' : 'bg-[#1F80FF]/5'
        : modoOscuro ? 'bg-gray-900' : 'bg-white'
    }`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-[10px] font-semibold tracking-wide uppercase text-gray-400">{label}</div>
        {Icon && (
          <span className={`p-1.5 rounded-lg ${modoOscuro ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
            <Icon className="w-3.5 h-3.5" />
          </span>
        )}
      </div>
      <div className={`text-lg sm:text-xl font-semibold tabular-nums ${color}`}>{value}</div>
      {hint && <div className="text-[11px] text-gray-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function PanelSkeleton({ box, modoOscuro }) {
  const pulse = modoOscuro ? 'bg-gray-800 animate-pulse' : 'bg-gray-200 animate-pulse';
  return (
    <div className="space-y-4">
      <div className={`rounded-2xl border overflow-hidden ${box}`}>
        <div className={`h-14 border-b ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`} />
        <div className="p-4">
          <div className={`h-40 rounded-xl ${pulse}`} />
        </div>
      </div>
      <div className={`rounded-2xl border overflow-hidden ${box}`}>
        <div className={`grid grid-cols-2 lg:grid-cols-4 gap-px ${modoOscuro ? 'bg-gray-800' : 'bg-gray-100'}`}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={`h-20 ${modoOscuro ? 'bg-gray-900' : 'bg-white'}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function CollectionReportPanel({
  rango,
  modoOscuro,
  box,
  textPri,
  textMuted,
  divide,
  hasProfesional,
  hasCobranzasEssential = false,
  secureFetch,
  formatMoney,
}) {
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [rentabilidad, setRentabilidad] = useState(null);
  const [tendencias, setTendencias] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [error, setError] = useState('');

  const green = modoOscuro ? 'text-green-300' : 'text-green-700';
  const red = modoOscuro ? 'text-red-300' : 'text-red-700';
  const amber = modoOscuro ? 'text-amber-300' : 'text-amber-700';
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { desde, hasta } = rango;
      const [kpiRes, trendRes, resumenRes] = await Promise.all([
        secureFetch(`/api/reportes/cobranzas-kpis?desde=${desde}&hasta=${hasta}`),
        secureFetch('/api/reportes/cobranzas-tendencias?meses=6'),
        secureFetch('/api/reportes/cobranzas-resumen'),
      ]);

      const kpiData = await kpiRes.json();
      if (!kpiRes.ok) throw new Error(kpiData.message || 'Error al cargar KPIs');
      setKpis(kpiData);

      if (trendRes.ok) setTendencias(await trendRes.json());
      else setTendencias(null);

      if (resumenRes.ok) setResumen(await resumenRes.json());
      else setResumen(null);

      if (hasProfesional) {
        const rentRes = await secureFetch(`/api/reportes/rentabilidad-clientes?desde=${desde}&hasta=${hasta}&limit=12`);
        if (rentRes.ok) setRentabilidad(await rentRes.json());
        else setRentabilidad(null);
      } else {
        setRentabilidad(null);
      }
    } catch (err) {
      setError(err.message);
      setKpis(null);
      setRentabilidad(null);
      setTendencias(null);
      setResumen(null);
    } finally {
      setLoading(false);
    }
  }, [rango, hasProfesional, secureFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async (format = 'csv') => {
    if (!hasProfesional) return;
    const setBusy = format === 'pdf' ? setExportingPdf : setExporting;
    setBusy(true);
    try {
      const { desde, hasta } = rango;
      await downloadCollectionExport(secureFetch, { desde, hasta, format });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <PanelSkeleton box={box} modoOscuro={modoOscuro} />;

  if (error && !kpis) {
    return (
      <div className={`rounded-2xl border px-4 py-8 text-center ${box}`}>
        <FiAlertTriangle className={`w-8 h-8 mx-auto mb-2 ${red}`} />
        <p className={`text-sm ${red}`}>{error}</p>
      </div>
    );
  }

  const summary = kpis?.summary;
  const aging = kpis?.aging;
  const agingTotal = aging
    ? Object.values(aging.counts || {}).reduce((a, b) => a + b, 0)
    : 0;
  const varTasa = resumen?.variacion?.tasaCobranza ?? 0;

  return (
    <div className="space-y-4">
      <CollectionImpactPanel
        secureFetch={secureFetch}
        hasProfesional={hasProfesional}
        modoOscuro={modoOscuro}
        box={box}
        textPri={textPri}
        textSec={textMuted}
        textMuted={textMuted}
        rango={rango}
      />

      {!hasProfesional && !hasCobranzasEssential && (
        <PlanUpgradeCard
          compact
          description="El resumen de cobranzas está en plan Básico+. DSO, tendencias y exportación en Profesional+."
          planLabel="Básico"
          modoOscuro={modoOscuro}
        />
      )}

      {summary && (
        <div className={`rounded-xl border px-3 py-2.5 sm:px-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 ${box}`}>
          <p className={`text-sm font-medium tabular-nums ${textPri}`}>
            Tasa {summary.tasaCobranza}% en el período
            {varTasa !== 0 && (
              <span className={`text-xs font-normal ${varTasa >= 0 ? green : red}`}>
                {' '}({varTasa > 0 ? '+' : ''}{varTasa} pp vs anterior)
              </span>
            )}
            {summary.vencidas > 0 && (
              <span className={`text-xs font-normal ${textMuted}`}>
                {' · '}{summary.vencidas} vencida(s) hoy
              </span>
            )}
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/cobranzas"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-[#1F80FF] text-white hover:bg-[#004AAD]"
            >
              Ir a cobrar
              <FiArrowRight className="w-3 h-3" />
            </Link>
            {hasProfesional && (
              <>
                <button
                  type="button"
                  onClick={() => handleExport('csv')}
                  disabled={exporting || exportingPdf}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border disabled:opacity-50 ${
                    modoOscuro ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <FiDownload className="w-3 h-3" />
                  {exporting ? '…' : 'CSV'}
                </button>
                <button
                  type="button"
                  onClick={() => handleExport('pdf')}
                  disabled={exporting || exportingPdf}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border disabled:opacity-50 ${
                    modoOscuro ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <FiDownload className="w-3 h-3" />
                  {exportingPdf ? '…' : 'PDF'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {summary && (
        <section className={`rounded-2xl border overflow-hidden ${box}`}>
          <div className={`px-4 py-3 border-b ${divide}`}>
            <div className="flex items-center gap-2">
              <FiActivity className={`w-4 h-4 ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`} />
              <div>
                <h2 className={`text-sm font-semibold ${textPri}`}>Snapshot del período</h2>
                <p className={`text-xs ${textMuted}`}>
                  {rango.desde} – {rango.hasta} · cartera al día de hoy
                </p>
              </div>
            </div>
          </div>
          <div className={`grid grid-cols-2 lg:grid-cols-4 gap-px ${modoOscuro ? 'bg-gray-800' : 'bg-gray-100'}`}>
            <KpiCard
              label="Tasa de cobranza"
              value={`${summary.tasaCobranza}%`}
              hint={`${formatMoney(summary.cobrado)} cobrado en el período`}
              color={summary.tasaCobranza >= 80 ? green : amber}
              modoOscuro={modoOscuro}
              icon={FiTrendingUp}
              highlight
            />
            <KpiCard
              label="Pendiente actual"
              value={formatMoney(summary.pendienteActual)}
              hint={`${summary.impagas} comprobante(s) impago(s)`}
              color={summary.pendienteActual > 0 ? amber : green}
              modoOscuro={modoOscuro}
              icon={FiDollarSign}
            />
            <KpiCard
              label="Vencidas hoy"
              value={summary.vencidas}
              hint="Fuera de plazo"
              color={summary.vencidas > 0 ? red : green}
              modoOscuro={modoOscuro}
              icon={FiClock}
            />
            <KpiCard
              label={hasProfesional ? 'DSO cartera' : 'Promesas'}
              value={hasProfesional ? `${summary.dso} días` : summary.promesasVencidas}
              hint={hasProfesional
                ? 'Días de venta en cartera'
                : `${summary.promesasVencidas} promesa(s) vencida(s)`}
              color={hasProfesional && summary.dso > 45 ? amber : textPri}
              modoOscuro={modoOscuro}
              icon={FiAlertTriangle}
            />
          </div>
        </section>
      )}

      {tendencias?.points?.length > 0 && (
        <section className={`rounded-xl border overflow-hidden ${box}`}>
          <div className={`px-3 py-2.5 sm:px-4 border-b ${divide}`}>
            <h2 className={`text-sm font-semibold ${textPri}`}>Últimos 6 meses</h2>
            <p className={`text-[11px] ${textMuted}`}>Tasa y DSO por mes · contexto histórico</p>
          </div>
          <div className="p-3 sm:p-4">
            <CollectionTrendChart
              points={tendencias.points}
              modoOscuro={modoOscuro}
              textPri={textPri}
              textMuted={textMuted}
              showDso={hasProfesional}
            />
          </div>
        </section>
      )}

      {aging && agingTotal > 0 && (
        <section className={`rounded-2xl border overflow-hidden ${box}`}>
          <div className={`px-4 py-3 border-b ${divide}`}>
            <h2 className={`text-sm font-semibold ${textPri}`}>Aging de cartera</h2>
            <p className={`text-xs ${textMuted}`}>{agingTotal} comprobante(s) con saldo</p>
          </div>
          <div className="p-4 sm:p-5 space-y-4">
            <div className={`flex h-2.5 rounded-full overflow-hidden ${modoOscuro ? 'bg-gray-800' : 'bg-gray-100'}`}>
              {Object.entries(AGING_COLORS).map(([key, color]) => {
                const count = aging.counts[key] || 0;
                const pct = agingTotal > 0 ? (count / agingTotal) * 100 : 0;
                if (pct <= 0) return null;
                return (
                  <div
                    key={key}
                    className={`${color} transition-all`}
                    style={{ width: `${pct}%` }}
                    title={`${AGING_LABELS[key]}: ${count}`}
                  />
                );
              })}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {Object.entries(AGING_LABELS).map(([key, label]) => (
                <div
                  key={key}
                  className={`rounded-xl border px-3 py-3 transition-colors ${
                    modoOscuro ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50/80'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${AGING_COLORS[key]}`} />
                    <span className={`text-xs font-medium ${textPri}`}>{label}</span>
                  </div>
                  <div className={`text-base font-bold tabular-nums ${textPri}`}>{aging.counts[key] || 0}</div>
                  <div className={`text-[11px] mt-0.5 ${textMuted}`}>{formatMoney(aging.montos[key] || 0)}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {kpis?.topMorosos?.length > 0 && (
          <section className={`rounded-2xl border overflow-hidden ${box}`}>
            <div className={`px-4 py-3 border-b flex items-center justify-between ${divide}`}>
              <h2 className={`text-sm font-semibold flex items-center gap-2 ${textPri}`}>
                <FiAlertTriangle className={`w-4 h-4 ${red}`} />
                Mayor atraso
              </h2>
              <Link to="/cobranzas" className="text-xs font-semibold text-[#1F80FF] hover:underline inline-flex items-center gap-1">
                Ir a cobrar
                <FiArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <ul className={`divide-y ${divide}`}>
              {kpis.topMorosos.map((row, idx) => (
                <li key={`${row.numero}-${row.cliente}`} className="px-4 py-3 flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-bold shrink-0 ${
                    modoOscuro ? 'bg-red-900/40 text-red-200' : 'bg-red-50 text-red-700'
                  }`}>
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold truncate ${textPri}`}>{row.cliente}</div>
                    <div className={`text-xs ${textMuted}`}>N°{row.numero} · {row.daysOverdue}d vencida</div>
                  </div>
                  <span className={`text-sm font-bold tabular-nums shrink-0 ${textPri}`}>
                    {formatMoney(row.saldoArs)}
                  </span>
                  <Link
                    to={`/cobranzas?q=${encodeURIComponent(row.cliente)}`}
                    className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold text-[#1F80FF] hover:underline"
                  >
                    Cobrar
                    <FiArrowRight className="w-3 h-3" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {hasProfesional && kpis?.lowScoreClients?.length > 0 && (
          <section className={`rounded-2xl border overflow-hidden ${box}`}>
            <div className={`px-4 py-3 border-b ${divide}`}>
              <h2 className={`text-sm font-semibold ${textPri}`}>Clientes de riesgo</h2>
              <p className={`text-xs ${textMuted}`}>Score de cobranza bajo (&lt;40)</p>
            </div>
            <ul className={`divide-y ${divide}`}>
              {kpis.lowScoreClients.map((c) => (
                <li key={c.cliente} className="px-4 py-3 flex items-center justify-between gap-3">
                  <span className={`text-sm font-medium truncate ${textPri}`}>{c.cliente}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    {c.impagas > 0 && (
                      <span className={`text-[10px] ${textMuted}`}>{c.impagas} imp.</span>
                    )}
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-lg ${
                      modoOscuro ? 'bg-red-900/40 text-red-200' : 'bg-red-50 text-red-700'
                    }`}>
                      {c.score}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {hasProfesional && rentabilidad?.clientes?.length > 0 && (
        <section className={`rounded-2xl border overflow-hidden ${box}`}>
          <div className={`px-4 py-3 border-b ${divide}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <h2 className={`text-sm font-semibold flex items-center gap-2 ${textPri}`}>
                  <FiUsers className="w-4 h-4" />
                  Rentabilidad por cliente
                </h2>
                <p className={`text-xs mt-0.5 max-w-xl ${textMuted}`}>{rentabilidad.nota}</p>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                {rentabilidad.pctEgresosAsignados > 0 && (
                  <div className={`text-right rounded-xl px-3 py-2 ${modoOscuro ? 'bg-gray-800' : 'bg-gray-50'}`}>
                    <div className={`text-[10px] uppercase ${textMuted}`}>Egresos asignados</div>
                    <div className={`text-sm font-bold tabular-nums ${textPri}`}>{rentabilidad.pctEgresosAsignados}%</div>
                  </div>
                )}
                <div className={`text-right rounded-xl px-3 py-2 ${modoOscuro ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className={`text-[10px] uppercase ${textMuted}`}>Margen global</div>
                  <div className={`text-sm font-bold tabular-nums ${rentabilidad.margenGlobal >= 0 ? green : red}`}>
                    {formatMoney(rentabilidad.margenGlobal)}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className={modoOscuro ? 'bg-gray-800/80 text-gray-400' : 'bg-gray-50 text-gray-500'}>
                <tr>
                  <th className="py-3 pl-4 pr-2 text-left font-semibold w-10">#</th>
                  <th className="py-3 px-2 text-left font-semibold">Cliente</th>
                  <th className="py-3 px-2 text-right font-semibold">Cobrado</th>
                  <th className="py-3 px-2 text-right font-semibold" title="Con egresos asignados al cliente en Egresos">Margen</th>
                  <th className="py-3 px-2 text-right font-semibold text-gray-400">Referencia</th>
                  <th className="py-3 pr-4 pl-2 text-right font-semibold">Eficacia</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${divide}`}>
                {rentabilidad.clientes.map((c, idx) => (
                  <tr key={c.cliente} className={modoOscuro ? 'hover:bg-gray-800/40' : 'hover:bg-gray-50/80'}>
                    <td className={`py-3 pl-4 pr-2 text-xs font-semibold ${textMuted}`}>{idx + 1}</td>
                    <td className="py-3 px-2">
                      <div className={`font-medium ${textPri}`}>{c.cliente}</div>
                      <div className={`text-xs ${textMuted}`}>{c.cantidadTrabajos} venta(s) · {c.sharePct}% share</div>
                    </td>
                    <td className={`py-3 px-2 text-right tabular-nums font-semibold ${textPri}`}>
                      {formatMoney(c.cobrado)}
                    </td>
                    <td className="py-3 px-2 text-right tabular-nums">
                      {c.tieneCostosDirectos && c.margenReal != null ? (
                        <>
                          <div className={`text-base font-bold ${c.margenReal >= 0 ? green : red}`}>
                            {formatMoney(c.margenReal)}
                          </div>
                          {c.margenRealPct != null && (
                            <div className={`text-[11px] ${textMuted}`}>{c.margenRealPct}% real</div>
                          )}
                          <div className={`text-[10px] ${textMuted}`}>
                            {formatMoney(c.costoDirecto)} costo directo
                          </div>
                        </>
                      ) : (
                        <>
                          <div className={`text-base font-bold ${c.margenEstimado >= 0 ? green : red}`}>
                            {formatMoney(c.margenEstimado)}
                          </div>
                          <span className={`inline-flex mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                            modoOscuro ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'
                          }`}>
                            Estimado
                          </span>
                        </>
                      )}
                    </td>
                    <td className={`py-3 px-2 text-right tabular-nums text-xs ${textMuted}`}>
                      {c.tieneCostosDirectos ? (
                        <>
                          <div>{formatMoney(c.margenEstimado)}</div>
                          <span className={`inline-flex mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                            modoOscuro ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'
                          }`}>
                            Est. {c.margenPct}%
                          </span>
                        </>
                      ) : (
                        <span title="Asigná egresos al cliente para ver margen real">—</span>
                      )}
                    </td>
                    <td className={`py-3 pr-4 pl-2 text-right`}>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold tabular-nums ${
                        c.eficaciaCobro >= 80
                          ? modoOscuro ? 'bg-green-900/40 text-green-200' : 'bg-green-50 text-green-800'
                          : modoOscuro ? 'bg-amber-900/40 text-amber-200' : 'bg-amber-50 text-amber-800'
                      }`}>
                        {c.eficaciaCobro}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={`px-4 py-3 border-t ${divide}`}>
            <Link to="/clientes-detalle" className="text-xs font-semibold text-[#1F80FF] hover:underline inline-flex items-center gap-1">
              Ver ranking completo
              <FiArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </section>
      )}

      {!hasProfesional && (
        <PlanUpgradeCard
          title="Inteligencia de cobranza en Profesional"
          description="Desbloqueá DSO histórico, export CSV, margen por cliente y alertas automáticas."
          bullets={[
            'Tendencias de 6 meses con DSO por mes',
            'Export CSV para tu contador',
            'Alertas email cuando sube el DSO',
          ]}
          planLabel="Profesional"
          modoOscuro={modoOscuro}
          box={box}
          textPri={textPri}
          textMuted={textMuted}
        />
      )}
    </div>
  );
}
